import React, { useState, useEffect, useCallback } from 'react';
import { isEngineeringEnvironment, isFeatureEnabled } from '../../../../AppSettings/environmentConfig';
import { useRuntimeConfig } from '../../../../__settings__/runtimeConfig';
import UninvoicedWorkspace from './UninvoicedWorkspace';
import './UninvoicedPanel.css';
import InvoiceService, {
  UninvoicedSummaryClient,
  UninvoicedItem,
  UninvoicedInvoiceScope,
} from '../../../../AppSettings/services/InvoiceService';

export interface UninvoicedPanelProps {
  onClose: () => void;
  onCreateInvoice: (clientId: string, matterId: string, scope?: UninvoicedInvoiceScope) => void;
  /** Create one invoice for all uninvoiced items across all matters for this client */
  onCreateInvoiceForClient?: (clientId: string, scope?: UninvoicedInvoiceScope) => void;
}

const SOURCE_LABELS: Record<string, string> = {
  billed_time: 'Time',
  fixed_fees: 'Fixed Fee',
  expenses: 'Expense',
  other_billing: 'Other',
  disbursements: 'Disbursement',
};

const SOURCE_TAG_CLASS: Record<string, string> = {
  billed_time: 'tag-time',
  fixed_fees: 'tag-fee',
  expenses: 'tag-expense',
  other_billing: 'tag-other',
  disbursements: 'tag-disbursement',
};

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (d: string | null) => {
  if (!d) return '';
  // Date-only (YYYY-MM-DD) must be built in local time; `new Date('2026-06-16')`
  // parses as UTC midnight, which renders a day earlier west of UTC (e.g. MST).
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d.trim());
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const UninvoicedPanel: React.FC<UninvoicedPanelProps> = ({ onClose, onCreateInvoice, onCreateInvoiceForClient }) => {
  const [clients, setClients] = useState<UninvoicedSummaryClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [expandedMatters, setExpandedMatters] = useState<Set<string>>(new Set());
  const [matterItems, setMatterItems] = useState<Record<string, UninvoicedItem[]>>({});
  const [loadingMatters, setLoadingMatters] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await InvoiceService.getUninvoicedSummary();
      setClients(data);
      if (data.length <= 5) {
        setExpandedClients(new Set(data.map((c) => c.clientid)));
      }
    } catch (err) {
      console.error('Failed to fetch uninvoiced summary:', err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const toggleClient = (clientId: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleMatter = async (matterId: string) => {
    const isCurrentlyExpanded = expandedMatters.has(matterId);
    setExpandedMatters((prev) => {
      const next = new Set(prev);
      if (isCurrentlyExpanded) next.delete(matterId);
      else next.add(matterId);
      return next;
    });

    if (!isCurrentlyExpanded && !matterItems[matterId]) {
      setLoadingMatters((prev) => new Set(prev).add(matterId));
      try {
        const items = await InvoiceService.getUninvoicedItems(matterId);
        setMatterItems((prev) => ({ ...prev, [matterId]: items }));
      } catch (err) {
        console.error('Failed to fetch uninvoiced items:', err);
        setMatterItems((prev) => ({ ...prev, [matterId]: [] }));
      } finally {
        setLoadingMatters((prev) => {
          const next = new Set(prev);
          next.delete(matterId);
          return next;
        });
      }
    }
  };

  const grandTotal = clients.reduce((sum, c) => sum + c.total_uninvoiced, 0);
  const totalItems = clients.reduce(
    (sum, c) => sum + c.matters.reduce((ms, m) => ms + m.uninvoiced_count, 0),
    0
  );

  return (
    <div className="uninvoiced-overlay" data-testid="uninvoiced-overlay" onClick={onClose}>
      <div
        className="uninvoiced-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="uninvoiced-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="uninvoiced-header">
          <h2 id="uninvoiced-modal-title">Uninvoiced Items</h2>
          <button
            type="button"
            className="uninvoiced-close-btn"
            onClick={onClose}
            aria-label="Close uninvoiced items"
            autoFocus
          >
            &times;
          </button>
        </div>

        {!loading && clients.length > 0 && (
          <div className="uninvoiced-summary-bar">
            <span>
              {totalItems} item{totalItems !== 1 ? 's' : ''} across{' '}
              {clients.length} client{clients.length !== 1 ? 's' : ''}
            </span>
            <span className="uninvoiced-summary-total">${fmt(grandTotal)}</span>
          </div>
        )}

        <div className="uninvoiced-content">
          {loading ? (
            <div className="uninvoiced-loading">
              <div className="uninvoiced-spinner" />
              <span>Loading...</span>
            </div>
          ) : clients.length === 0 ? (
            <div className="uninvoiced-empty">
              <p>No uninvoiced items found.</p>
              <p>All billable work has been invoiced.</p>
            </div>
          ) : (
            clients.map((client) => {
              const isClientExpanded = expandedClients.has(client.clientid);
              return (
                <div key={client.clientid} className="uninvoiced-client-group">
                  <div
                    className="uninvoiced-client-header"
                    onClick={() => toggleClient(client.clientid)}
                  >
                    <div className="uninvoiced-client-left">
                      <span className={`uninvoiced-chevron ${isClientExpanded ? 'expanded' : ''}`}>
                        &#9654;
                      </span>
                      <span className="uninvoiced-client-name">{client.client_name}</span>
                    </div>
                    <div className="uninvoiced-client-right">
                      {onCreateInvoiceForClient && (
                        <button
                          type="button"
                          className="uninvoiced-create-full-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCreateInvoiceForClient(client.clientid);
                          }}
                          title="Create one invoice for all uninvoiced items for this client"
                        >
                          Create full invoice
                        </button>
                      )}
                      <span className="uninvoiced-client-total">
                        ${fmt(client.total_uninvoiced)}
                      </span>
                    </div>
                  </div>

                  {isClientExpanded && (
                    <div className="uninvoiced-matter-list">
                      {client.matters.map((matter) => {
                        const isMatterExpanded = expandedMatters.has(matter.matterid);
                        const isMatterLoading = loadingMatters.has(matter.matterid);
                        const items = matterItems[matter.matterid];

                        return (
                          <div key={matter.matterid} className="uninvoiced-matter-block">
                            <div
                              className={`uninvoiced-matter-row ${isMatterExpanded ? 'expanded' : ''}`}
                              onClick={() => toggleMatter(matter.matterid)}
                            >
                              <div className="uninvoiced-matter-info">
                                <div className="uninvoiced-matter-name-row">
                                  <span className={`uninvoiced-chevron-sm ${isMatterExpanded ? 'expanded' : ''}`}>
                                    &#9654;
                                  </span>
                                  <span className="uninvoiced-matter-name">{matter.matter_name}</span>
                                </div>
                                <div className="uninvoiced-matter-stats">
                                  {Object.entries(matter.breakdown).map(([type, info]) => (
                                    <span key={type} className="uninvoiced-chip">
                                      {info.count} {SOURCE_LABELS[type] || type}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="uninvoiced-matter-right">
                                <div className="uninvoiced-matter-amount">
                                  ${fmt(matter.uninvoiced_total)}
                                </div>
                                <button
                                  type="button"
                                  className="uninvoiced-create-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onCreateInvoice(client.clientid, matter.matterid);
                                  }}
                                >
                                  Create Invoice
                                </button>
                              </div>
                            </div>

                            {isMatterExpanded && (
                              <div className="uninvoiced-items-list">
                                {isMatterLoading ? (
                                  <div className="uninvoiced-items-loading">
                                    <div className="uninvoiced-spinner-sm" />
                                    <span>Loading items...</span>
                                  </div>
                                ) : items && items.length > 0 ? (
                                  items.map((item) => (
                                    <div key={`${item.source_type}-${item.id}`} className="uninvoiced-item-row">
                                      <div className="uninvoiced-item-left">
                                        <span className={`uninvoiced-item-tag ${SOURCE_TAG_CLASS[item.source_type] || ''}`}>
                                          {SOURCE_LABELS[item.source_type] || item.source_type}
                                        </span>
                                        <span className="uninvoiced-item-desc">{item.description}</span>
                                      </div>
                                      <div className="uninvoiced-item-right">
                                        <span className="uninvoiced-item-amount">${fmt(item.amount)}</span>
                                        {item.date && (
                                          <span className="uninvoiced-item-date">{formatDate(item.date)}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="uninvoiced-items-empty">No items found</div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const UninvoicedEntry: React.FC<UninvoicedPanelProps> = (props) => {
  useRuntimeConfig();
  return isEngineeringEnvironment && !isFeatureEnabled('bids')
    ? <UninvoicedWorkspace {...props} />
    : <UninvoicedPanel {...props} />;
};

export default UninvoicedEntry;
